export function getDescendantIds(nodeId, edges) {
    const descendants = new Set();
    const pending = [nodeId];

    while (pending.length) {
        const parentId = pending.pop();
        edges.filter(edge => edge.source === parentId).forEach(edge => {
            if (edge.target !== nodeId && !descendants.has(edge.target)) {
                descendants.add(edge.target);
                pending.push(edge.target);
            }
        });
    }

    return descendants;
}

export function getHiddenNodeIds(collapsedIds, edges) {
    const hiddenIds = new Set();
    collapsedIds.forEach(id => {
        getDescendantIds(id, edges).forEach(childId => hiddenIds.add(childId));
    });
    return hiddenIds;
}

export function validateParentChange(nodeId, parentId, nodes, edges, maxDepth = 7) {
    if (!nodes.some(node => node.id === parentId)) return 'Please select a parent.';
    if (nodeId === parentId || getDescendantIds(nodeId, edges).has(parentId)) {
        return 'A member cannot report to itself or one of its descendants.';
    }

    const parentById = new Map(edges.map(edge => [edge.target, edge.source]));
    parentById.set(nodeId, parentId);

    for (const node of nodes) {
        const visited = new Set();
        let currentId = node.id;
        while (currentId) {
            if (visited.has(currentId)) return 'This parent would create a circular hierarchy.';
            visited.add(currentId);
            if (visited.size > maxDepth) return `The organization supports a maximum of ${maxDepth} levels.`;
            currentId = parentById.get(currentId);
        }
    }

    return null;
}

// Entity rows are visual levels; edges still represent the actual parent relationships.
const entityRows = { organization: 0, division: 1, department: 2, position: 3 };

// Reflow the full tree only after a structural edit; ordinary edits keep the reference layout.
export function layoutHierarchy(nodes, edges) {
    const nodesById = new Map(nodes.map(node => [node.id, node]));
    const childrenById = new Map(nodes.map(node => [node.id, []]));
    const childIds = new Set(edges.map(edge => edge.target));
    edges.forEach(edge => childrenById.get(edge.source)?.push(edge.target));
    const positions = new Map();
    let leafIndex = 0;

    function place(id, depth) {
        const children = childrenById.get(id) || [];
        const childPositions = children.map(childId => place(childId, depth + 1));
        const x = children.length
            ? (childPositions[0] + childPositions[childPositions.length - 1]) / 2
            : leafIndex++ * 240;
        const row = entityRows[nodesById.get(id)?.data?.entityType] ?? depth;
        positions.set(id, { x, y: row * 150 });
        return x;
    }

    nodes.filter(node => !childIds.has(node.id)).forEach(node => place(node.id, 0));
    return nodes.map(node => ({ ...node, position: positions.get(node.id) || node.position }));
}
